import { ReactNode } from "react";
import { errorNotice, listIcon, LoadingSpinner, successNotice } from "@pjs/core-ui";
import { AuthorisedDeviceModelState, authorisedDevices, AuthorisedDevicesState, IAuthorisedDevicesModel } from "@pjs/user-domain";
import { catchError, easeLoading, filter, map, of } from "@pjs/observables";
import { actionLogger } from "@pjs/a11y";
import { AsyncModel, createAsyncModelError, createAsyncModelPending, createAsyncModelResult, Immutable } from "@pjs/utilities";
import { IViewConfig } from "../interfaces/IViewConfig";
import { userUiI18n } from "../../i18n/UserUiI18n.const";
import { apiConfig } from "../../../ApiConfig";
import { ExternalServices } from "../../external-services/ExternalServices.component";
import { accountSettingsActions } from "../../actions/AccountSettingsActions.const";
import { View } from "../enums/View";
import { IStateNotice } from "../interfaces/IStateNotice";
import { AccountSettingsError } from "../AccountSettingsError.component";

export const externalServicesView: IViewConfig = {
    id: View.ExternalServices,
    page: {
        get title() {
            return userUiI18n.getString("external_services.title");
        },
        get description() {
            return userUiI18n.getString("external_services.description", { url: apiConfig.externalServicesLink() });
        }
    },
    navigation: {
        icon: listIcon,
        get label() {
            return userUiI18n.getString("settings_sidebar.buttons.external_services");
        }
    },
    component: () => {
        const getData = authorisedDevices.load().pipe(
            map((model: Immutable<IAuthorisedDevicesModel>) => {
                const externalServicesModel = {
                    authorisedDevices: model
                };

                return createAsyncModelResult(<ExternalServices model={externalServicesModel} actions={accountSettingsActions.externalServices} />);
            }),
            catchError(() => {
                const title = userUiI18n.getString("settings_view.loading_error.title");
                const content = userUiI18n.getString("settings_view.loading_error.content");
                const loggerMessage = userUiI18n.getString("external_services.logger.page_load_error");
                actionLogger.append(loggerMessage);

                return of(createAsyncModelError(<AccountSettingsError title={title} content={content} />));
            })
        );

        return easeLoading<AsyncModel<ReactNode, ReactNode, ReactNode>>(
            createAsyncModelPending(<LoadingSpinner className="uui-settings-view__loading-spinner" />),
            getData
        );
    },
    isEnabled: (userDetails, _accountSettings) => !userDetails.isTemporaryAccess && !userDetails.isExternal,
    state: () =>
        authorisedDevices.getDocument().model.state.pipe(
            filter((state) => {
                return (
                    state.descriptor === AuthorisedDevicesState.RevokeError ||
                    state.descriptor === AuthorisedDevicesState.RevokeSuccess ||
                    state.descriptor === AuthorisedDevicesState.LoadError
                );
            }),
            map((state): IStateNotice => {
                const modelState = state as AuthorisedDeviceModelState;

                if (modelState.descriptor === AuthorisedDevicesState.RevokeError) {
                    const deviceName = modelState.payload.activeDeviceName;

                    return {
                        type: errorNotice,
                        message: userUiI18n.getString("external_services.authorised_devices.revoke_error", {
                            device: deviceName
                        }),
                        ariaLabel: userUiI18n.getString("external_services.authorised_devices.a11y.revoke_error", {
                            device: deviceName
                        }),
                        actionLogMessage: userUiI18n.getString("external_services.authorised_devices.logger.revoke_error", {
                            device: deviceName
                        })
                    };
                }

                if (modelState.descriptor === AuthorisedDevicesState.RevokeSuccess) {
                    const deviceName = modelState.payload.activeDeviceName;

                    return {
                        type: successNotice,
                        message: userUiI18n.getString("external_services.authorised_devices.revoke_success", {
                            device: deviceName
                        }),
                        ariaLabel: userUiI18n.getString("external_services.authorised_devices.revoke_success", {
                            device: deviceName
                        }),
                        actionLogMessage: userUiI18n.getString("external_services.authorised_devices.logger.revoke_success", {
                            device: deviceName
                        })
                    };
                }

                return {
                    type: errorNotice,
                    message: userUiI18n.getString("external_services.authorised_devices.fetch_error"),
                    ariaLabel: userUiI18n.getString("external_services.authorised_devices.a11y.fetch_error"),
                    actionLogMessage: userUiI18n.getString("settings_view.notices.logger.load_error")
                };
            })
        ),
    clear: () => authorisedDevices.clear()
};
